import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="column-properties-editor"
//
// Handles property changes in the properties sidepanel
// Updates hidden form fields and triggers auto-save
//
// Usage:
//   .properties-content[
//     data-controller="column-properties-editor"
//     data-column-properties-editor-column-id-value="12345"
//   ]
//
export default class extends Controller {
  static values = {
    columnId: String,
    originalName: String,
    dataSetName: String
  }

  connect() {
    // Store the original column name when the controller connects
    const originalNameElement = this.element.querySelector('[data-original-column-name="true"]')
    if (originalNameElement) {
      this.originalNameValue = originalNameElement.textContent.trim()
    }

    // Store the data set name for translations
    const dataSetInput = document.querySelector('input[name*="[data_set]"]')
    if (dataSetInput) {
      this.dataSetNameValue = dataSetInput.value
    }

    logger.log('[Column Properties Editor] Connected - Original name:', this.originalNameValue)
  }

  updateProperty(event) {
    const input = event.target
    const propertyName = input.dataset.columnProperty || input.dataset.sheetProperty

    // For paste events, we need to wait for the value to be updated
    // setTimeout with 0 delay ensures the paste value is in the input
    if (event.type === 'paste') {
      setTimeout(() => {
        this.processPropertyUpdate(input, propertyName, input.value)
      }, 0)
      return
    }

    // Handle different input types
    const value = input.type === 'checkbox' ? input.checked.toString() : input.value
    logger.log(`[Column Properties Editor] updateProperty called: property="${propertyName}", inputType="${input.type}", value="${value}", checked="${input.checked}"`)
    this.processPropertyUpdate(input, propertyName, value)
  }

  processPropertyUpdate(input, propertyName, value) {
    logger.log(`[Column Properties Editor] processPropertyUpdate START: property="${propertyName}", value="${value}", columnId="${this.columnIdValue}"`)

    // Find the corresponding hidden field in the main form
    let hiddenField

    if (input.dataset.columnProperty) {
      // Column property - find in selected columns list
      const columnElement = document.querySelector(`[data-column-id="${this.columnIdValue}"]`)
      logger.log(`[Column Properties Editor] Column element found: ${!!columnElement}`)

      if (columnElement) {
        // Look for the specific input with matching data-column-data
        hiddenField = columnElement.querySelector(`input[data-column-data="${propertyName}"]`)
        logger.log(`[Column Properties Editor] Hidden field found: ${!!hiddenField}, currentValue="${hiddenField?.value}"`)
        
        if (!hiddenField) {
          logger.warn(`[Column Properties Editor] No input found with data-column-data="${propertyName}", trying name-based search`)
          hiddenField = columnElement.querySelector(`[name*="[${propertyName}]"]`)
        }
        
        // Special handling for title - also update visible column title, panel title, and preview
        if (propertyName === 'title') {
          const columnTitleElement = columnElement.querySelector('.column-title')
          if (columnTitleElement) {
            columnTitleElement.textContent = value
            logger.log("[Column Properties Editor] Updated visible column title")
          }

          // Also update the panel title with the column name
          const panelTitle = document.querySelector('[data-properties-sidepanel-target="panelTitle"]')
          if (panelTitle) {
            panelTitle.textContent = value
            logger.log("[Column Properties Editor] Updated panel title")
          }

          // Update preview header cell
          const previewHeader = this.element.querySelector('[data-column-preview="header"]')
          if (previewHeader) {
            previewHeader.textContent = value
            logger.log("[Column Properties Editor] Updated preview header")
          }
        }

        // Special handling for locked - update lock/unlock icon visibility
        if (propertyName === 'locked') {
          const lockIcon = columnElement.querySelector('[data-column-editor-v3-target="lockIcon"]')
          const unlockIcon = columnElement.querySelector('[data-column-editor-v3-target="unlockIcon"]')
          const isLocked = value === 'true' || value === true

          if (lockIcon) {
            lockIcon.style.display = isLocked ? '' : 'none'
          }
          if (unlockIcon) {
            unlockIcon.style.display = isLocked ? 'none' : ''
          }
          logger.log(`[Column Properties Editor] Updated lock icon visibility: ${isLocked}`)

          // Dispatch event for locked-cells-warning controller
          const lockedStateChangedEvent = new CustomEvent('column-locked-state-changed', {
            bubbles: true,
            detail: { columnId: this.columnIdValue, locked: isLocked }
          })
          this.element.dispatchEvent(lockedStateChangedEvent)
        }

        // Update preview cell colors when color properties change
        if (propertyName === 'header_background_color') {
          const previewHeader = this.element.querySelector('[data-column-preview="header"]')
          if (previewHeader) {
            previewHeader.style.backgroundColor = value
            logger.log("[Column Properties Editor] Updated preview header background")
          }
        } else if (propertyName === 'header_text_color') {
          const previewHeader = this.element.querySelector('[data-column-preview="header"]')
          if (previewHeader) {
            previewHeader.style.color = value
            logger.log("[Column Properties Editor] Updated preview header text color")
          }
        } else if (propertyName === 'column_background_color') {
          const previewData = this.element.querySelector('.preview-data-cell')
          if (previewData) {
            previewData.style.backgroundColor = value
            logger.log("[Column Properties Editor] Updated preview data cell background")
          }
        } else if (propertyName === 'column_text_color') {
          const previewData = this.element.querySelector('.preview-data-cell')
          if (previewData) {
            previewData.style.color = value
            logger.log("[Column Properties Editor] Updated preview data cell text color")
          }
        }
        
        // Special handling for color properties - sync color picker and text input
        if (propertyName.endsWith('_text')) {
          // Text input changed - update the color picker
          // Use regex with $ anchor to only remove _text at the END of the string
          // This is critical because property names like "header_text_color_text" contain "_text" twice
          const baseProperty = propertyName.replace(/_text$/, '')
          const colorPicker = this.element.querySelector(`input[data-column-property="${baseProperty}"]`)
          if (colorPicker) {
            colorPicker.value = value
            logger.log(`[Column Properties Editor] Synced color picker ${baseProperty} to: ${value}`)
          }

          // CRITICAL: Also update the preview for _text properties
          // The preview update code (lines 97-122) only runs for base properties,
          // so when typing/pasting into text input, we need to manually update preview
          if (baseProperty === 'header_background_color') {
            const previewHeader = this.element.querySelector('[data-column-preview="header"]')
            if (previewHeader) {
              previewHeader.style.backgroundColor = value
              logger.log("[Column Properties Editor] Updated preview header background (from text input)")
            }
          } else if (baseProperty === 'header_text_color') {
            const previewHeader = this.element.querySelector('[data-column-preview="header"]')
            if (previewHeader) {
              previewHeader.style.color = value
              logger.log("[Column Properties Editor] Updated preview header text color (from text input)")
            }
          } else if (baseProperty === 'column_background_color') {
            const previewData = this.element.querySelector('.preview-data-cell')
            if (previewData) {
              previewData.style.backgroundColor = value
              logger.log("[Column Properties Editor] Updated preview data cell background (from text input)")
            }
          } else if (baseProperty === 'column_text_color') {
            const previewData = this.element.querySelector('.preview-data-cell')
            if (previewData) {
              previewData.style.color = value
              logger.log("[Column Properties Editor] Updated preview data cell text color (from text input)")
            }
          }
        } else if (input.type === 'color') {
          // Color picker changed - update the text input
          const textProperty = propertyName + '_text'
          const textInput = this.element.querySelector(`input[data-column-property="${textProperty}"]`)
          if (textInput) {
            textInput.value = value
            logger.log(`[Column Properties Editor] Synced text input ${textProperty} to: ${value}`)
          }
        }
        
        if (!hiddenField && propertyName.endsWith('_text')) {
          // This is a text representation of a color, find the base color field
          // Use regex with $ anchor to only remove _text at the END of the string
          const baseProperty = propertyName.replace(/_text$/, '')
          // Search by data-column-data attribute first (more reliable)
          const baseField = columnElement.querySelector(`input[data-column-data="${baseProperty}"]`)
          if (baseField) {
            baseField.value = value
            logger.log(`[Column Properties Editor] Updated base color field ${baseProperty}`)
            // Use the base field as the hidden field for auto-save
            hiddenField = baseField
          }
        }
      } else {
        logger.warn("[Column Properties Editor] Column element not found:", this.columnIdValue)
      }
    } else if (input.dataset.sheetProperty) {
      // Sheet property - use generic lookup by data-sheet-data attribute
      // This works for: sheet_title, password, and any future sheet properties
      if (propertyName.startsWith('parameter_')) {
        // Parameters use special naming: parameter_X -> data-sheet-data="parameter_X"
        const paramName = propertyName.replace('parameter_', '')
        hiddenField = document.querySelector(`input[data-sheet-data="parameter_${paramName}"], select[data-sheet-data="parameter_${paramName}"], textarea[data-sheet-data="parameter_${paramName}"]`)
      } else {
        // Generic lookup for all other sheet properties (sheet_title, password, etc.)
        hiddenField = document.querySelector(`input[data-sheet-data="${propertyName}"], select[data-sheet-data="${propertyName}"], textarea[data-sheet-data="${propertyName}"]`)
      }

      // Special visual updates for sheet_title (keep existing behavior)
      if (propertyName === 'sheet_title' && hiddenField) {
        // Update the visible sheet name in the active tab
        const sheetNameElement = document.querySelector('[data-sheet-name="true"]')
        if (sheetNameElement) {
          sheetNameElement.textContent = value
          logger.log("[Column Properties Editor] Updated visible sheet name in header")
        }

        // Update the sheet title in the manage sheets panel
        const sheetId = hiddenField.closest('[data-properties-sidepanel-sheet-id-value]')?.dataset.propertiesSidepanelSheetIdValue
        if (sheetId) {
          const sheetItem = document.querySelector(`.manage-sheets-panel .sheet-item[data-sheet-id="${sheetId}"]`)
          if (sheetItem) {
            const sheetTitleElement = sheetItem.querySelector('.sheet-title')
            if (sheetTitleElement) {
              // Update just the text, preserving the icon
              const icon = sheetTitleElement.querySelector('i')
              sheetTitleElement.textContent = value || 'גיליון'
              if (icon) {
                sheetTitleElement.prepend(icon)
              }
              logger.log("[Column Properties Editor] Updated sheet name in manage sheets panel")
            }
          }
        }
      }
    }
    
    if (hiddenField) {
      // CRITICAL: Update the hidden field value
      // Use setAttribute as well as direct assignment to ensure the value is committed to the DOM
      hiddenField.value = value
      hiddenField.setAttribute('value', value)

      logger.log(`[Column Properties Editor] Updated ${propertyName} to: ${value}`)
      logger.log(`[Column Properties Editor] Hidden field value after update: ${hiddenField.value}`)
      logger.log(`[Column Properties Editor] Hidden field name: ${hiddenField.name}`)

      // Trigger auto-save with a small delay to ensure DOM is updated
      setTimeout(() => {
        logger.log("[Column Properties Editor] Dispatching auto-save event...")
        const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
          bubbles: true,
          detail: { source: 'properties-editor', property: propertyName }
        })
        this.element.dispatchEvent(autoSaveEvent)
      }, 10) // 10ms delay to ensure DOM is updated
    } else {
      logger.error("[Column Properties Editor] ✗ Could not find hidden field for:", propertyName)
    }
  }
  
  updateParameter(event) {
    const input = event.currentTarget
    const paramName = input.dataset.columnParameter
    const parameterName = input.dataset.parameterName
    const paramType = input.dataset.columnParameterType
    let value = input.value

    logger.log("[Column Properties Editor] Updating parameter:", paramName, "=", value)

    // Handle select_array type: convert newline-separated text to JSON array
    if (paramType === 'select_array') {
      const lines = value.split('\n').map(line => line.trim()).filter(line => line.length > 0)
      value = JSON.stringify(lines)
      logger.log("[Column Properties Editor] Converted select_array to JSON:", value)
    }

    // Find the hidden field by name
    const hiddenField = document.querySelector(`input[name="${parameterName}"]`)
    if (hiddenField) {
      hiddenField.value = value
      logger.log("[Column Properties Editor] Updated hidden parameter field")

      // NOTE: Do NOT update the main 'value' field here!
      // The 'parameters' nested hash in the form submission will be processed by
      // the model's parameters= setter which properly serializes to YAML.
      // Previously this code was corrupting the value field with malformed strings.

      // Auto-update column title for data_point parameters
      // Handles both 'data_point' (data fields) and '*_data_point' (employees, etc.)
      if (paramName && (paramName === 'data_point' || paramName.endsWith('_data_point'))) {
        this.autoUpdateColumnTitle(paramName, value, input)
      }

      // Auto-update format code when selecting a date-type parameter option
      this.autoUpdateFormatCode(input, value)

      // Dispatch auto-save event
      const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
        bubbles: true,
        detail: { source: 'parameter-change', paramName, value }
      })
      this.element.dispatchEvent(autoSaveEvent)
    } else {
      logger.warn("[Column Properties Editor] Hidden parameter field not found:", parameterName)
    }
  }

  autoUpdateColumnTitle(paramName, selectedValue, selectElement) {
    // Only auto-update for data_point type parameters
    // Handles both 'data_point' (data fields) and '*_data_point' (employees, etc.)
    if (!(paramName === 'data_point' || paramName.endsWith('_data_point'))) return

    // Get the current title input
    const titleInput = this.element.querySelector('input[data-column-property="title"]')
    if (!titleInput) return

    const currentTitle = titleInput.value.trim()
    const originalName = this.originalNameValue

    if (!originalName) {
      logger.log('[Column Properties Editor] No original name stored, skipping auto-update')
      return
    }

    // Check if the current title matches the original name or original name with data point in parentheses
    // Pattern: "Original Name" or "Original Name (Something)"
    const originalNamePattern = new RegExp(`^${this.escapeRegExp(originalName)}(\\s*\\([^)]+\\))?$`)
    const userHasChangedTitle = !originalNamePattern.test(currentTitle)

    if (userHasChangedTitle) {
      logger.log('[Column Properties Editor] User has manually changed the title, skipping auto-update')
      return
    }

    // Get the selected option's text (translated label)
    const selectedOption = selectElement.querySelector(`option[value="${selectedValue}"]`)
    const dataPointLabel = selectedOption ? selectedOption.textContent.trim() : selectedValue

    // Get all options to check if this is the first/default one
    const allOptions = Array.from(selectElement.querySelectorAll('option'))
    const isFirstOption = allOptions.length > 0 && allOptions[0].value === selectedValue

    // Update the title
    let newTitle
    if (isFirstOption) {
      // If it's the first/default option, just use the original name
      newTitle = originalName
    } else {
      // Otherwise, append the data point label in parentheses
      newTitle = `${originalName} (${dataPointLabel})`
    }

    logger.log('[Column Properties Editor] Auto-updating title from:', currentTitle, 'to:', newTitle)

    // Update the title input value
    titleInput.value = newTitle

    // Trigger the updateProperty logic for the title
    titleInput.dispatchEvent(new Event('input', { bubbles: true }))
  }

  escapeRegExp(string) {
    // Escape special regex characters
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  autoUpdateFormatCode(parameterInput, selectedValue) {
    // Check if the parameter has option_types defined
    const optionTypesAttr = parameterInput.dataset.columnParameterOptionTypes
    if (!optionTypesAttr) return

    let optionTypes
    try {
      optionTypes = JSON.parse(optionTypesAttr)
    } catch (e) {
      logger.warn('[Column Properties Editor] Failed to parse option_types:', e)
      return
    }

    // Find the format code dropdown in the properties panel
    const formatCodeSelect = this.element.querySelector('select[data-column-property="format_code"]')
    if (!formatCodeSelect) return

    // Determine the new format based on the selected data point type
    // When data point changes, format should ALWAYS reflect its type
    const selectedType = optionTypes[selectedValue]
    let newFormat
    if (selectedType === 'date') {
      newFormat = 'dd/mm/yyyy'
    } else if (selectedType === 'time') {
      newFormat = 'hh:mm:ss'
    } else if (selectedType === 'text') {
      // Text type (phone numbers, ID numbers) - prevents leading zeros from being stripped
      newFormat = '@'
    } else {
      // Non-date/time/text data points (like עודכן על ידי) should use General
      newFormat = 'General'
    }

    // Only update if the format is actually changing
    if (formatCodeSelect.value === newFormat) {
      logger.log('[Column Properties Editor] Format code already', newFormat, '- no change needed')
      return
    }

    formatCodeSelect.value = newFormat
    logger.log('[Column Properties Editor] Auto-updated format_code to:', newFormat)

    // Trigger the update event to sync with hidden field and auto-save
    formatCodeSelect.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
